import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { fmtDate, STATUS_TR } from "@/utils/format";
import { Factory, Package, Plus, Clock } from "lucide-react";
import AttachmentList from "@/components/AttachmentList";
import PageHeader from "@/components/PageHeader";
import Pager, { usePagination } from "@/components/Pager";

function ProduceRow({ orderId, itemId, it, onDone }) {
  const required = Number(it.produce_qty ?? it.quantity ?? 0);
  // Local produced count that starts from props and, after a successful
  // PATCH, is upgraded to the backend's returned value. This means the
  // row's own `remaining` / `isDone` derivations are ALWAYS driven by
  // the authoritative Mongo write, not by any hidden-flag heuristic.
  const [localProduced, setLocalProduced] = useState(Number(it.produced_qty ?? 0));
  const produced = Math.max(localProduced, Number(it.produced_qty ?? 0));
  const remaining = Math.max(0, required - produced);
  const pct = required > 0 ? Math.min(100, (produced / required) * 100) : 0;
  const [addQty, setAddQty] = useState("");
  const [busy, setBusy] = useState(false);

  // Overdue check
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = !!(it.due_date && it.status !== "completed" && it.due_date < today);
  const dueSoon = !!(
    it.due_date && it.status !== "completed" && !isOverdue &&
    (new Date(it.due_date) - new Date()) < 3 * 24 * 3600 * 1000
  );

  // Both submit + finishAll route through this common core. The RULE
  // (per user directive): frontend derives visibility purely from the
  // backend's returned produced_qty vs quantity — no locally-invented
  // hidden flag. The row disappears because the returned produced_qty
  // catches up to quantity, and the parent load() re-fetches the fresh
  // filtered list. A hard reload runs 600ms later as a belt-and-
  // suspenders guarantee that any stale cache layer is nuked, so the
  // item never "comes back" on the next navigation.
  const runProduce = async (produceQty, successLabel, hardReloadAfter) => {
    if (produceQty <= 0) { onDone?.(); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/orders/${orderId}/items/${itemId}/produce?qty=${produceQty}`,
      );
      toast.success(successLabel);
      setAddQty("");
      // Optimistic override — the row now reads its produced/quantity
      // straight from the backend response, so if the backend says the
      // item is at its ceiling, the `isDone` check kicks in on the very
      // next render and hides the row. No local invented state.
      const returned = (data?.items || []).find((r) => (r.id || "") === itemId);
      if (returned) {
        setLocalProduced(Number(returned.produced_qty ?? 0));
      }
      if (hardReloadAfter) {
        setTimeout(() => window.location.reload(), 600);
        return;
      }
      onDone?.();
      setTimeout(() => onDone?.(), 300);
    } catch (e) {
      const detail = String(e.response?.data?.detail || "");
      if (detail.includes("tamamland") || detail.includes("Yeni hazırlanacak")) {
        setLocalProduced(required);
        onDone?.();
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally { setBusy(false); }
  };

  const submit = async () => {
    const q = Number(addQty);
    if (!q || q <= 0) { toast.error("Geçerli bir miktar girin"); return; }
    if (remaining <= 1e-6) { setLocalProduced(required); onDone?.(); return; }
    if (q > remaining + 1e-6) { toast.error(`En fazla ${remaining} girebilirsiniz`); return; }
    const willComplete = produced + q + 1e-6 >= required;
    await runProduce(q, `+${q} ${it.unit || ""} üretildi · Depoya aktarıldı`, willComplete);
  };

  const finishAll = async () => {
    if (remaining <= 1e-6) { setLocalProduced(required); onDone?.(); return; }
    await runProduce(remaining, "Tamamlandı · Depoya aktarıldı", true);
  };

  const isDone = remaining <= 0;
  const isPartial = produced > 0 && remaining > 0;

  // Row visibility is derived STRICTLY from qty math:
  //   remaining (= required - produced) drops to 0 → row disappears.
  // Both the backend-returned produced_qty and the local optimistic
  // value flow into the same `produced` variable, so this single check
  // covers stale state, fresh writes, and post-PATCH updates.
  if (isDone) return null;

  return (
    <div className="p-4 border-t border-neutral-200 first:border-t-0" data-testid={`prod-item-${orderId}-${itemId}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2">
            {isOverdue && (
              <span
                className="inline-block w-2.5 h-2.5 rounded-full bg-[#FF3B30] animate-pulse"
                title="Termin süresi geçti"
                data-testid={`overdue-dot-${orderId}-${itemId}`}
              />
            )}
            <Package size={14} /> {it.product_name}
          </div>
          <div className="text-xs font-mono text-neutral-500 mt-1">{it.sku}</div>
          {isPartial && (
            <div
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border-2 border-[#FFCC00] bg-[#FFF7D6] text-[#7a5f00]"
              data-testid={`prod-partial-badge-${orderId}-${itemId}`}
            >
              Kısmi Üretildi · {produced}/{required} {it.unit || ""}
            </div>
          )}
          {it.due_date && (
            <div
              className={`text-xs font-mono mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 border ${
                isOverdue
                  ? "border-[#FF3B30] text-[#FF3B30] font-bold"
                  : dueSoon
                  ? "border-[#FFCC00] text-[#7a5f00]"
                  : "border-neutral-300 text-neutral-600"
              }`}
              data-testid={`due-date-${orderId}-${itemId}`}
            >
              <Clock size={10} /> Termin: {it.due_date}
              {isOverdue && <span className="ml-1 uppercase tracking-widest">· Gecikti</span>}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase text-neutral-500">Toplam Üretim</div>
          <div className="text-2xl font-mono font-bold text-[#FF3B30]">
            {required} <span className="text-sm font-normal text-neutral-500">{it.unit}</span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs font-mono">
        <span className="text-neutral-500">İlerleme</span>
        <span data-testid={`prod-progress-text-${orderId}-${itemId}`}>
          <span className={isDone ? "text-[#00A650] font-bold" : "text-[#09090B] font-bold"}>{produced}</span>
          <span className="text-neutral-500"> / {required} {it.unit}</span>
        </span>
      </div>
      <div className="mt-1 h-2 border-2 border-[#09090B] bg-white overflow-hidden">
        <div
          className={`h-full ${isDone ? "bg-[#00A650]" : "bg-[#FF3B30]"}`}
          style={{ width: `${pct}%` }}
          data-testid={`prod-progress-bar-${orderId}-${itemId}`}
        />
      </div>

      {isDone ? (
        <div className="mt-3 inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-widest border-2 border-[#00A650] text-[#00A650]">
          Üretim tamamlandı
        </div>
      ) : (
        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[10px] font-mono uppercase text-neutral-500">Bu partide üretilen</label>
            <Input
              type="number" min="0" step="0.01" max={remaining}
              value={addQty} onChange={(e) => setAddQty(e.target.value)}
              placeholder={`En fazla ${remaining}`}
              className="rounded-none border-2 border-[#09090B] font-mono h-10"
              data-testid={`prod-qty-input-${orderId}-${itemId}`}
            />
          </div>
          <Button
            className="rounded-none bg-[#09090B] hover:bg-[#FF3B30] h-10"
            onClick={submit} disabled={busy}
            data-testid={`prod-add-btn-${orderId}-${itemId}`}
          >
            <Plus size={14} className="mr-1" /> Ekle
          </Button>
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B] h-10"
            onClick={finishAll} disabled={busy}
            data-testid={`prod-finish-btn-${orderId}-${itemId}`}
          >
            Tümünü Bitir ({remaining})
          </Button>
        </div>
      )}

      {(it.production_log || []).length > 0 && (
        <details className="mt-3 text-xs font-mono">
          <summary className="cursor-pointer text-neutral-500 hover:text-[#09090B]">
            Üretim geçmişi ({(it.production_log || []).length})
          </summary>
          <div className="mt-2 space-y-1 pl-2 border-l-2 border-neutral-300">
            {(it.production_log || []).map((l, i) => (
              <div key={i} className="text-neutral-600">
                +{l.qty} {it.unit} · {new Date(l.at).toLocaleString("tr-TR")} · {l.by}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function ProductionView() {
  const [orders, setOrders] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      // Cache-bust so the browser never serves a stale response after a
      // fresh PATCH. Coupled with the SW's `no-store` on `/api/*`, this
      // guarantees the newly-produced qty shows up immediately.
      const { data } = await api.get(`/orders?_ts=${Date.now()}`);
      // QUANTITY-BASED FILTER, status-independent (per business rule).
      // A production row is visible iff produced_qty < quantity. The
      // moment production catches up, the row vanishes — no toast, no
      // "completed" badge, just gone. Item-level `status` is ignored on
      // purpose; the only hard flag we still respect is `cancelled` since
      // that means the row was administratively deleted.
      const productionOnly = data
        .filter((o) =>
          o.status !== "cancelled" &&
          o.status !== "partially_cancelled",
        )
        .map((o) => ({
          ...o,
          items: (o.items || []).filter((it) => {
            if (it.allocation !== "production") return false;
            if (it.status === "cancelled") return false;
            const required = Number(it.produce_qty ?? it.quantity ?? 0);
            const produced = Number(it.produced_qty ?? 0);
            return produced + 1e-6 < required;
          }),
        }))
        .filter((o) => o.items.length > 0);
      setOrders(productionOnly);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const pending = orders.filter((o) => o.status !== "completed" && o.status !== "shipped");

  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = pending.reduce(
    (n, o) => n + o.items.filter((it) => it.status !== "completed" && it.due_date && it.due_date < today).length,
    0,
  );

  // Sort orders so those with overdue items appear first
  pending.sort((a, b) => {
    const aOver = a.items.some((it) => it.status !== "completed" && it.due_date && it.due_date < today);
    const bOver = b.items.some((it) => it.status !== "completed" && it.due_date && it.due_date < today);
    if (aOver !== bOver) return aOver ? -1 : 1;
    return 0;
  });

  const { page, setPage, totalPages, paged } = usePagination(pending, 10);

  return (
    <div className="space-y-6" data-testid="production-page">
      <PageHeader
        kicker="Üretim Emirleri"
        title="ÜRETİM"
        subtitle="Kısmi üretim destekli. Her parti için üretilen miktarı girin; kalan miktar sıfırlanınca kalem otomatik tamamlanır. Fiyat bilgisi gösterilmez."
        border="border-[#FF3B30]"
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex flex-col gap-2 items-end">
            <div className="flex items-center gap-2 px-4 py-1.5 border-2 border-[#FF3B30] text-[#FF3B30] font-mono uppercase text-xs">
              <Factory size={14} /> Aktif: {pending.length}
            </div>
            {overdueCount > 0 && (
              <div
                className="flex items-center gap-2 px-4 py-1.5 border-2 border-[#FF3B30] bg-[#FF3B30] text-white font-mono uppercase text-xs"
                data-testid="overdue-badge"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
                Gecikmiş: {overdueCount}
              </div>
            )}
          </div>
        }
      />

      <div className="flex gap-2" data-testid="production-tabs">
        <a href="/uretim" className="px-4 py-2 border-2 border-[#09090B] font-mono text-xs uppercase tracking-widest bg-[#FF3B30] text-white" data-testid="tab-order-based">Sipariş Bazlı</a>
        <a href="/uretim/urun-bazli" className="px-4 py-2 border-2 border-[#09090B] font-mono text-xs uppercase tracking-widest bg-white hover:bg-neutral-100" data-testid="tab-product-based">Ürün Bazlı</a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {paged.map((o) => (
          <div key={o.id} className="bg-white border-2 border-[#09090B] border-l-[6px] border-l-[#FF3B30]" data-testid={`prod-order-${o.id}`}>
            <div className="p-4 border-b-2 border-[#09090B] flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{fmtDate(o.created_at)}</div>
                <div className="text-lg font-mono font-bold">{o.order_number}</div>
                <div className="text-sm text-neutral-600">{o.customer_name}</div>
              </div>
              <div className="text-[10px] font-mono uppercase border border-[#FF3B30] text-[#FF3B30] px-2 py-0.5">{STATUS_TR[o.status]}</div>
            </div>
            <div>
              {o.items.map((it, i) => (
                <ProduceRow key={it.id || i} orderId={o.id} itemId={it.id || i} it={it} onDone={load} />
              ))}
            </div>
            {o.attachments && o.attachments.length > 0 && (
              <div className="p-3 border-t-2 border-neutral-200 bg-neutral-50">
                <AttachmentList
                  endpoint={`/orders/${o.id}/attachments`}
                  attachments={o.attachments}
                  compact
                />
              </div>
            )}
          </div>
        ))}
        {pending.length === 0 && (
          <div className="col-span-2 bg-white border-2 border-dashed border-[#09090B] p-12 text-center text-neutral-500">
            Bekleyen üretim emri yok.
          </div>
        )}
      </div>
      <Pager page={page} setPage={setPage} totalPages={totalPages} testid="prod-pager" />
    </div>
  );
}
